import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  LockClosedIcon,
  KeyIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  EnvelopeIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { 
  setupPIN, 
  getSecurityStatus, 
  sendEmailVerificationOTP, 
  verifyEmailOTP,
} from '@/api/pinSecurityApi'
import toast from 'react-hot-toast'

// Form schemas
const setupPINSchema = z.object({
  pin: z.string()
    .length(4, 'PIN must be exactly 4 digits')
    .regex(/^\d{4}$/, 'PIN must contain only numbers'),
  confirmPin: z.string()
    .length(4, 'PIN must be exactly 4 digits'),
  newPassword: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain uppercase, lowercase, and number'),
  confirmPassword: z.string()
}).refine(data => data.pin === data.confirmPin, {
  message: "PINs don't match",
  path: ["confirmPin"],
}).refine(data => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
})

const emailVerificationSchema = z.object({
  otpCode: z.string()
    .length(6, 'OTP must be exactly 6 digits')
    .regex(/^\d{6}$/, 'OTP must contain only numbers'),
})

type SetupPINData = z.infer<typeof setupPINSchema>
type EmailVerificationData = z.infer<typeof emailVerificationSchema>

interface FirstTimeSetupProps {
  // Component now handles navigation internally based on user role
}

const FirstTimeSetup: React.FC<FirstTimeSetupProps> = () => {
  const { user } = useAuth()
  const navigate = useNavigate()

  // Helper function to get redirect path based on user role
  const getRedirectPath = (userRole: string): string => {
    const role = userRole?.toLowerCase() || ''

    // Check for super admin
    if (role === 'super_admin' || role.includes('super') || role.includes('platform')) {
      return '/admin/dashboard'
    }

    // Check for school admin
    if (role === 'school_admin' || (role.includes('school') && role.includes('admin'))) {
      return '/schoolAdmin/dashboard'
    }

    // Check for teacher
    if (role === 'teacher' || role.includes('teacher')) {
      return '/teacher/dashboard'
    }

    // Check for parent
    if (role === 'parent' || role.includes('parent') || role.includes('guardian')) {
      return '/parent/dashboard'
    }

    // Check for driver
    if (role === 'driver' || role.includes('driver')) {
      return '/driver/dashboard'
    }

    // Check for student
    if (role === 'student' || role.includes('student') || role.includes('pupil') || role.includes('learner')) {
      return '/student/dashboard'
    }

    // Default fallback to school admin dashboard
    return '/schoolAdmin/dashboard'
  }
  const [currentStep, setCurrentStep] = useState<'pin-setup' | 'email-verify'>('pin-setup')
  const [showPIN, setShowPIN] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [securityStatus, setSecurityStatus] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [otpSent, setOtpSent] = useState(false)
  const [otpExpiry, setOtpExpiry] = useState<Date | null>(null)
  const [timeRemaining, setTimeRemaining] = useState(0)

  const pinSetupForm = useForm<SetupPINData>({
    resolver: zodResolver(setupPINSchema),
    defaultValues: {
      pin: '',
      confirmPin: '',
      newPassword: '',
      confirmPassword: '',
    }
  })

  const emailVerifyForm = useForm<EmailVerificationData>({
    resolver: zodResolver(emailVerificationSchema),
    defaultValues: {
      otpCode: '',
    }
  })

  useEffect(() => {
    fetchSecurityStatus()
  }, [])

  // Countdown timer for OTP expiry
  useEffect(() => {
    if (otpExpiry) {
      const interval = setInterval(() => {
        const now = new Date()
        const remaining = Math.max(0, Math.floor((otpExpiry.getTime() - now.getTime()) / 1000))
        setTimeRemaining(remaining)
        
        if (remaining === 0) {
          setOtpSent(false)
          setOtpExpiry(null)
        }
      }, 1000)
      
      return () => clearInterval(interval)
    }
  }, [otpExpiry])

  const fetchSecurityStatus = async () => {
    try {
      const response = await getSecurityStatus()
      setSecurityStatus(response.data)
      
      // If PIN is already set up, skip to email verification
      if (response.data.hasPIN && !response.data.emailVerified) {
        setCurrentStep('email-verify')
      }
    } catch (error: any) {
      console.error('Failed to fetch security status:', error)
      toast.error('Failed to load security status')
    }
  }

  const handlePINSetup = async (data: SetupPINData) => {
    setLoading(true)
    try {
       await setupPIN({
        pin: data.pin,
        newPassword: data.newPassword
      })
      
      toast.success('PIN and password set up successfully! Now verify your email.')
      setCurrentStep('email-verify')
      
    } catch (error: any) {
      console.error('Failed to setup PIN:', error)
      toast.error(error.response?.data?.message || 'Failed to setup PIN')
    } finally {
      setLoading(false)
    }
  }

  const handleSendEmailOTP = async () => {
    setLoading(true)
    try {
      const response = await sendEmailVerificationOTP()
      setOtpSent(true)
      setOtpExpiry(new Date(response.data.expiresAt))
      toast.success('Verification code sent to your email!')
      
    } catch (error: any) {
      console.error('Failed to send email OTP:', error)
      toast.error(error.response?.data?.message || 'Failed to send verification code')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyEmail = async (data: EmailVerificationData) => {
    setLoading(true)
    try {
      await verifyEmailOTP(data)
      toast.success('Email verified successfully! Setting up your account...')
      
      // Complete the setup process and redirect based on role
      const redirectPath = getRedirectPath(user?.role || 'SUPER_ADMIN')
      setTimeout(() => {
        navigate(redirectPath, { replace: true })
        // Refresh page to ensure clean state after onboarding
        window.location.reload()
      }, 1500) // Small delay to show success message
      
    } catch (error: any) {
      console.error('Failed to verify email:', error)
      toast.error(error.response?.data?.message || 'Invalid or expired verification code')
    } finally {
      setLoading(false)
    }
  }

  const validatePIN = (pin: string): { isValid: boolean; message?: string } => {
    if (pin.length !== 4) return { isValid: false, message: 'PIN must be 4 digits' }
    if (!/^\d{4}$/.test(pin)) return { isValid: false, message: 'PIN must contain only numbers' }
    
    const weakPINs = ['0000', '1111', '2222', '3333', '4444', '5555', '6666', '7777', '8888', '9999', '1234', '4321']
    if (weakPINs.includes(pin)) return { isValid: false, message: 'PIN is too weak' }
    
    return { isValid: true }
  }

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <ShieldCheckIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Security Setup</h1>
                <p className="text-blue-100">Welcome {user?.firstName}! Let's secure your account</p>
              </div>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="px-8 py-4 bg-gray-50 border-b">
            <div className="flex items-center space-x-4">
              <div className={`flex items-center space-x-2 ${currentStep === 'pin-setup' ? 'text-blue-600' : 'text-green-600'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                  currentStep === 'pin-setup' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white'
                }`}>
                  {currentStep === 'email-verify' ? <CheckCircleIcon className="h-4 w-4" /> : '1'}
                </div>
                <span className="text-sm font-medium">PIN & Password</span>
              </div>
              
              <ArrowRightIcon className="h-4 w-4 text-gray-400" />
              
              <div className={`flex items-center space-x-2 ${
                currentStep === 'email-verify' ? 'text-blue-600' : 
                securityStatus?.emailVerified ? 'text-green-600' : 'text-gray-400'
              }`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                  currentStep === 'email-verify' ? 'bg-blue-600 text-white' : 
                  securityStatus?.emailVerified ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-600'
                }`}>
                  {securityStatus?.emailVerified ? <CheckCircleIcon className="h-4 w-4" /> : '2'}
                </div>
                <span className="text-sm font-medium">Email Verification</span>
              </div>
            </div>
          </div>

          <div className="p-8">
            <AnimatePresence mode="wait">
              {/* PIN Setup Step */}
              {currentStep === 'pin-setup' && (
                <motion.div
                  key="pin-setup"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="text-center mb-6">
                    <KeyIcon className="h-12 w-12 text-blue-600 mx-auto mb-3" />
                    <h2 className="text-lg font-bold text-gray-900">Set Up Your Security</h2>
                    <p className="text-gray-600">Create a 4-digit PIN and secure password for your account</p>
                  </div>

                  <form onSubmit={pinSetupForm.handleSubmit(handlePINSetup)} className="space-y-4">
                    {/* PIN Section */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-gray-900 flex items-center">
                        <LockClosedIcon className="h-4 w-4 mr-2 text-blue-600" />
                        4-Digit Security PIN
                      </h3>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="relative">
                          <Input
                            {...pinSetupForm.register('pin')}
                            type={showPIN ? 'text' : 'password'}
                            label="Create PIN"
                            placeholder="4 digits"
                            maxLength={4}
                            className="text-center text-lg font-mono tracking-widest"
                            error={pinSetupForm.formState.errors.pin?.message}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '').slice(0, 4)
                              pinSetupForm.setValue('pin', value)
                              
                              // Validate PIN in real-time
                              if (value.length === 4) {
                                const validation = validatePIN(value)
                                if (!validation.isValid) {
                                  pinSetupForm.setError('pin', { message: validation.message })
                                }
                              }
                            }}
                          />
                        </div>

                        <div className="relative">
                          <Input
                            {...pinSetupForm.register('confirmPin')}
                            type={showPIN ? 'text' : 'password'}
                            label="Confirm PIN"
                            placeholder="4 digits"
                            maxLength={4}
                            className="text-center text-lg font-mono tracking-widest"
                            error={pinSetupForm.formState.errors.confirmPin?.message}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '').slice(0, 4)
                              pinSetupForm.setValue('confirmPin', value)
                            }}
                          />
                        </div>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="showPIN"
                          checked={showPIN}
                          onChange={(e) => setShowPIN(e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="showPIN" className="ml-2 text-sm text-gray-700">
                          Show PIN
                        </label>
                      </div>
                    </div>

                    {/* Password Section */}
                    <div className="space-y-4 pt-4 border-t">
                      <h3 className="text-sm font-semibold text-gray-900 flex items-center">
                        <KeyIcon className="h-4 w-4 mr-2 text-green-600" />
                        Account Password
                      </h3>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="relative">
                          <Input
                            {...pinSetupForm.register('newPassword')}
                            type={showPassword ? 'text' : 'password'}
                            label="New Password"
                            placeholder="Strong password"
                            error={pinSetupForm.formState.errors.newPassword?.message}
                          />
                        </div>

                        <div className="relative">
                          <Input
                            {...pinSetupForm.register('confirmPassword')}
                            type={showPassword ? 'text' : 'password'}
                            label="Confirm Password"
                            placeholder="Repeat password"
                            error={pinSetupForm.formState.errors.confirmPassword?.message}
                          />
                        </div>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="showPassword"
                          checked={showPassword}
                          onChange={(e) => setShowPassword(e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="showPassword" className="ml-2 text-sm text-gray-700">
                          Show passwords
                        </label>
                      </div>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-medium text-yellow-900">Security Requirements</h4>
                          <div className="text-sm text-yellow-700 mt-1 space-y-1">
                            <p>• PIN: 4 unique digits (avoid 1234, 0000, etc.)</p>
                            <p>• Password: 8+ characters with uppercase, lowercase, and numbers</p>
                            <p>• These credentials will be required for sensitive operations</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      disabled={loading}
                    >
                      {loading ? (
                        <div className="flex items-center justify-center space-x-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Setting up security...</span>
                        </div>
                      ) : (
                        <>
                          <LockClosedIcon className="h-4 w-4 mr-2" />
                          Complete PIN & Password Setup
                        </>
                      )}
                    </Button>
                  </form>
                </motion.div>
              )}

              {/* Email Verification Step */}
              {currentStep === 'email-verify' && (
                <motion.div
                  key="email-verify"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="text-center mb-6">
                    <EnvelopeIcon className="h-12 w-12 text-green-600 mx-auto mb-3" />
                    <h2 className="text-lg font-bold text-gray-900">Verify Your Email</h2>
                    <p className="text-gray-600">We'll send a 6-digit code to <span className="font-medium">{user?.email}</span></p>
                  </div>

                  {!otpSent ? (
                    <div className="space-y-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                          <EnvelopeIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                          <div>
                            <h4 className="text-sm font-medium text-blue-900">Email Verification Required</h4>
                            <p className="text-sm text-blue-700 mt-1">
                              Verify your email to receive important notifications and security alerts.
                            </p>
                          </div>
                        </div>
                      </div>

                      <Button
                        onClick={handleSendEmailOTP}
                        className="w-full bg-green-600 hover:bg-green-700"
                        disabled={loading}
                      >
                        {loading ? (
                          <div className="flex items-center justify-center space-x-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Sending code...</span>
                          </div>
                        ) : (
                          <>
                            <EnvelopeIcon className="h-4 w-4 mr-2" />
                            Send Verification Code
                          </>
                        )}
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={emailVerifyForm.handleSubmit(handleVerifyEmail)} className="space-y-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center space-x-2">
                          <CheckCircleIcon className="h-5 w-5 text-green-600" />
                          <span className="text-sm font-medium text-green-900">Code sent to your email!</span>
                        </div>
                        <p className="text-sm text-green-700 mt-1">
                          Check your inbox for the 6-digit verification code
                        </p>
                        {timeRemaining > 0 && (
                          <p className="text-xs text-green-600 mt-2">
                            Code expires in: {formatTime(timeRemaining)}
                          </p>
                        )}
                      </div>

                      <Input
                        {...emailVerifyForm.register('otpCode')}
                        label="Verification Code"
                        placeholder="Enter 6-digit code"
                        className="text-center text-lg font-mono tracking-widest"
                        maxLength={6}
                        error={emailVerifyForm.formState.errors.otpCode?.message}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                          emailVerifyForm.setValue('otpCode', value)
                        }}
                      />

                      <div className="flex space-x-3">
                        <Button
                          type="button"
                          onClick={handleSendEmailOTP}
                          variant="secondary"
                          className="flex-1"
                          disabled={loading || timeRemaining > 540} // Disable for first 60 seconds
                        >
                          {loading ? 'Sending...' : 'Resend Code'}
                        </Button>
                        
                        <Button
                          type="submit"
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          disabled={loading || emailVerifyForm.watch('otpCode').length !== 6}
                        >
                          {loading ? (
                            <div className="flex items-center justify-center space-x-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              <span>Verifying...</span>
                            </div>
                          ) : (
                            <>
                              <CheckCircleIcon className="h-4 w-4 mr-2" />
                              Verify Email
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  )}

                  <button
                    onClick={() => {
                      toast.success('Setup completed! Welcome to SmartTag School.')
                      const redirectPath = getRedirectPath(user?.role || 'SUPER_ADMIN')
                      setTimeout(() => {
                        navigate(redirectPath, { replace: true })
                        // Refresh page to ensure clean state after onboarding
                        window.location.reload()
                      }, 1000)
                    }}
                    className="w-full mt-4 text-sm text-gray-600 hover:text-gray-800"
                  >
                    Skip email verification for now →
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default FirstTimeSetup